import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import {
  Button,
  InfoCard,
  InfoCardEditContent,
  InfoCardEditDivider,
  InfoCardFooter,
  InfoCardHeader,
  SettingsPageHeader,
} from '@/components/ui';
import { apiClient } from '@/api/axios-instance';
import { getGetTurnTrackerConfigQueryKey, useGetTurnTrackerConfig } from '@/api/generated/turn-tracker/turn-tracker';
import { useGetServices } from '@/api/generated/services/services';
import { useGetServiceCategories } from '@/api/generated/service-categories/service-categories';
import type { PatchTurnTrackerConfigBody } from '@/api/generated/schemas/patchTurnTrackerConfigBody';
import { InfoCardSkeleton } from '@/components/ui/info-card-skeleton';
import { AdvancedTurnSettingsCard } from '@/components/turn-tracker/settings/advanced-turn-settings-card';
import { SimpleTurnSettingsCard } from '@/components/turn-tracker/settings/simple-turn-settings-card';
import { SimpleServicesPreviewTable } from '@/components/turn-tracker/settings/simple-services-preview-table';
import { StartOverPasscodeCard } from '@/components/turn-tracker/settings/start-over-passcode-card';
import { useStaffPermissionAccess } from '@/hooks/use-staff-permission-access';
import { TurnTrackerSettingsSummary } from '@/components/turn-tracker/settings/turn-tracker-general-card';
import { TurnTrackerModeCard } from '@/components/turn-tracker/settings/turn-tracker-mode-card';
import { rebaseTurnTrackerSettingsForm } from '@/components/turn-tracker/settings/rebase-turn-tracker-settings-form';
import type {
  ServiceCategory,
  TurnTrackerConfig,
  TurnTrackerSettingsFormData,
} from '@/components/turn-tracker/settings/turn-tracker-settings-types';
import { BusinessSetupPageShell } from './business-setup/components/business-setup-page-shell';
import { formatDocumentTitle } from '@/lib/document-title';

export const Route = createFileRoute('/_auth/$orgId/settings/turn-tracker')({
  component: TurnTrackerSettings,
});

function createDefaultFormData(): TurnTrackerSettingsFormData {
  return {
    isEnabled: false,
    turnCalculationMode: 'SIMPLE',
    turnEquivalenceEnabled: false,
    turnEquivalenceThreshold: '0.5',
    basePricePerTurn: '',
    zeroTurnEnabled: false,
    zeroTurnThreshold: '',
    simpleTurnThreshold: '35',
    hasStartOverPasscode: false,
    startOverPasscode: '',
    priorityCategoryEnabled: false,
    priorityCategoryMultiplier: '0.5',
    simpleAppointmentRule: 'SAME',
    priorityCategoryIds: [],
  };
}

function configToFormData(data: TurnTrackerConfig | null): TurnTrackerSettingsFormData {
  if (!data) return createDefaultFormData();

  return {
    isEnabled: data.isEnabled,
    turnCalculationMode: data.turnCalculationMode,
    turnEquivalenceEnabled: data.turnEquivalenceEnabled,
    turnEquivalenceThreshold: String(data.turnEquivalenceThreshold),
    basePricePerTurn: data.basePricePerTurn ? String(data.basePricePerTurn) : '',
    zeroTurnEnabled: data.zeroTurnThreshold !== null,
    zeroTurnThreshold: data.zeroTurnThreshold ? String(data.zeroTurnThreshold) : '',
    simpleTurnThreshold: String(data.simpleTurnThreshold ?? 35),
    hasStartOverPasscode: data.hasStartOverPasscode,
    startOverPasscode: '',
    priorityCategoryEnabled: data.priorityCategoryEnabled,
    priorityCategoryMultiplier: String(data.priorityCategoryMultiplier),
    simpleAppointmentRule: data.simpleAppointmentRule ?? 'SAME',
    priorityCategoryIds: data.priorityCategoryIds,
  };
}

function TurnTrackerSettings() {
  const { orgId } = useParams({ from: '/_auth/$orgId/settings/turn-tracker' });
  const queryClient = useQueryClient();
  const { data: servicesResponse } = useGetServices();
  const activeServices = useMemo(
    () => (servicesResponse?.data ?? []).filter((service) => service.isActive !== false),
    [servicesResponse?.data]
  );
  // Cached reads: coming back paints the saved settings at once (categories share the Turns board's cache). The
  // edit form is a copy taken on Edit; a newer copy arriving meanwhile only updates fields the owner hasn't changed.
  // One retry for a blip; after that the error card (never an editable default form).
  const configQuery = useGetTurnTrackerConfig({ query: { retry: 1 } });
  const categoriesQuery = useGetServiceCategories();
  const categories: ServiceCategory[] = categoriesQuery.data?.data ?? [];
  const savedConfig = configQuery.data?.data as TurnTrackerConfig | null | undefined;
  const savedFormData = useMemo(() => configToFormData(savedConfig ?? null), [savedConfig]);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isPasscodeDirty, setIsPasscodeDirty] = useState(false);
  const { isAdmin: canChangePasscode } = useStaffPermissionAccess();
  const [formData, setFormData] = useState<TurnTrackerSettingsFormData>(createDefaultFormData);
  const editedFrom = useRef(savedFormData);
  useEffect(() => {
    const previous = editedFrom.current;
    editedFrom.current = savedFormData;
    if (isEditing && previous !== savedFormData) {
      setFormData((current) => rebaseTurnTrackerSettingsForm(savedFormData, current, previous));
    }
  }, [isEditing, savedFormData]);

  // A failed load must not fall through to the default ("off") form — saving that would overwrite
  // the real config. Show an error card with Retry instead.
  const isLoading = configQuery.isPending;
  const loadFailed = configQuery.isError && configQuery.data === undefined;
  const retryLoad = () => void configQuery.refetch();

  // The PATCH answers with the saved config: it becomes the cached copy (no re-read).
  const applySavedConfig = (response: { success: boolean; data: TurnTrackerConfig }) => {
    queryClient.setQueryData(getGetTurnTrackerConfigQueryKey(), response);
    return configToFormData(response.data);
  };

  const saveConfig = async (payload: PatchTurnTrackerConfigBody) => {
    const response = await apiClient.patch<{ success: boolean; data: TurnTrackerConfig }>(
      '/turn-tracker/config',
      payload
    );
    return applySavedConfig(response.data);
  };

  const handleEditClick = () => {
    setFormData(savedFormData);
    setIsPasscodeDirty(false);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFormData(savedFormData);
    setIsPasscodeDirty(false);
    setIsEditing(false);
  };

  const buildPayload = (): PatchTurnTrackerConfigBody | null => {
    const shouldUpdatePasscode = isPasscodeDirty;
    const isAdvancedMode = formData.turnCalculationMode === 'ADVANCED';
    const basePriceValue = parseFloat(formData.basePricePerTurn);
    const zeroThresholdValue = parseFloat(formData.zeroTurnThreshold);
    const simpleThresholdValue = parseFloat(formData.simpleTurnThreshold);
    const equivalenceValue = parseFloat(formData.turnEquivalenceThreshold);
    const priorityMultiplierValue = parseFloat(formData.priorityCategoryMultiplier);

    if (
      shouldUpdatePasscode &&
      formData.startOverPasscode.length > 0 &&
      formData.startOverPasscode.length !== 6
    ) {
      toast.error('Start Over passcode must be empty or exactly 6 digits');
      return null;
    }

    if (
      isAdvancedMode &&
      formData.turnEquivalenceEnabled &&
      (Number.isNaN(equivalenceValue) || equivalenceValue < 0.1 || equivalenceValue > 5)
    ) {
      toast.error('Equivalence threshold must be between 0.1 and 5');
      return null;
    }

    if (
      isAdvancedMode &&
      formData.basePricePerTurn &&
      (Number.isNaN(basePriceValue) || basePriceValue < 1 || basePriceValue > 9999)
    ) {
      toast.error('Base price per turn must be between $1 and $9,999');
      return null;
    }

    if (
      isAdvancedMode &&
      formData.zeroTurnEnabled &&
      (Number.isNaN(zeroThresholdValue) || zeroThresholdValue < 1 || zeroThresholdValue > 9999)
    ) {
      toast.error('Zero turn threshold must be between $1 and $9,999');
      return null;
    }

    if (
      !isAdvancedMode &&
      (Number.isNaN(simpleThresholdValue) ||
        simpleThresholdValue < 0 ||
        simpleThresholdValue > 9999)
    ) {
      toast.error('Simple threshold must be between $0 and $9,999');
      return null;
    }

    if (
      isAdvancedMode &&
      formData.priorityCategoryEnabled &&
      (Number.isNaN(priorityMultiplierValue) ||
        priorityMultiplierValue < 0.1 ||
        priorityMultiplierValue > 1)
    ) {
      toast.error('Priority multiplier must be between 0.1 and 1');
      return null;
    }

    return {
      turnCalculationMode: formData.turnCalculationMode,
      ...(shouldUpdatePasscode && {
        startOverPasscode:
          formData.startOverPasscode.length === 6 ? formData.startOverPasscode : null,
      }),
      ...(isAdvancedMode
        ? {
            turnEquivalenceEnabled: formData.turnEquivalenceEnabled,
            ...(formData.turnEquivalenceEnabled && {
              turnEquivalenceThreshold: equivalenceValue,
            }),
            basePricePerTurn:
              !Number.isNaN(basePriceValue) && basePriceValue > 0 ? basePriceValue : null,
            zeroTurnThreshold:
              formData.zeroTurnEnabled &&
              !Number.isNaN(zeroThresholdValue) &&
              zeroThresholdValue > 0
                ? zeroThresholdValue
                : null,
            priorityCategoryEnabled: formData.priorityCategoryEnabled,
            ...(formData.priorityCategoryEnabled && {
              priorityCategoryMultiplier: priorityMultiplierValue,
              priorityCategoryIds: formData.priorityCategoryIds,
            }),
          }
        : {
            simpleTurnThreshold: simpleThresholdValue,
            simpleAppointmentRule: formData.simpleAppointmentRule,
          }),
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) return;

    setIsSaving(true);
    try {
      await saveConfig(payload);
      setIsPasscodeDirty(false);
      setIsEditing(false);
      toast.success('Turn tracker settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Turn Tracker Settings')}</title>
      </Helmet>

      <BusinessSetupPageShell orgId={orgId} activePath="turn-tracker">
        <SettingsPageHeader
          title="Turn Tracker"
          description="Configure how the turn tracking system works for walk-in customers and service assignments."
        />

        {isLoading ? (
          <InfoCardSkeleton label="Loading turn tracker settings…" fields={4} className="max-w-3xl" />
        ) : loadFailed ? (
          <InfoCard className="max-w-3xl">
            <div role="alert">
              <p className="text-[16px] text-clovie-text">Couldn&apos;t load turn tracker settings.</p>
              <Button
                variant="outline"
                className="mt-4 min-h-11"
                onClick={retryLoad}
                disabled={configQuery.isFetching}
              >
                {configQuery.isFetching ? 'Retrying…' : 'Retry'}
              </Button>
            </div>
          </InfoCard>
        ) : (
          <InfoCard className="max-w-3xl">
            {isEditing ? (
              <>
                <InfoCardHeader title="General" showDivider={false} />
                <InfoCardEditDivider />
                <InfoCardEditContent>
                  {/* Turns on/off is the owner's switch on How you use Hayven; this card edits the rules while it is on. */}
                  {formData.isEnabled && (
                    <>
                      <TurnTrackerModeCard
                        value={formData.turnCalculationMode}
                        onChange={(turnCalculationMode) =>
                          setFormData({ ...formData, turnCalculationMode })
                        }
                      />

                      {formData.turnCalculationMode === 'SIMPLE' ? (
                        <SimpleTurnSettingsCard
                          formData={formData}
                          services={activeServices}
                          onChange={setFormData}
                        />
                      ) : (
                        <AdvancedTurnSettingsCard
                          formData={formData}
                          categories={categories}
                          onChange={setFormData}
                        />
                      )}

                      {/* The passcode guards Start over / reorder, so only administrators change it. */}
                      {canChangePasscode && (
                        <StartOverPasscodeCard
                          formData={formData}
                          onChange={(nextFormData) => {
                            setFormData(nextFormData);
                            setIsPasscodeDirty(true);
                          }}
                        />
                      )}
                    </>
                  )}
                </InfoCardEditContent>

                {formData.isEnabled && (
                  <InfoCardFooter>
                    <Button variant="cancel" onClick={handleCancel} disabled={isSaving}>
                      Cancel
                    </Button>
                    <Button
                      className="bg-clovie-primary text-white hover:bg-clovie-primary-hover"
                      onClick={handleSave}
                      disabled={isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                  </InfoCardFooter>
                )}
              </>
            ) : (
              <>
                <InfoCardHeader
                  title="General"
                  onEdit={savedFormData.isEnabled ? handleEditClick : undefined}
                />
                <div className="space-y-5">
                  {savedFormData.isEnabled && (
                    <TurnTrackerSettingsSummary formData={savedFormData} categories={categories} />
                  )}
                  {savedFormData.isEnabled && savedFormData.turnCalculationMode === 'SIMPLE' && (
                    <div className="border-t border-clovie-border-soft pt-5">
                      <SimpleServicesPreviewTable
                        services={activeServices}
                        config={{
                          turnCalculationMode: 'SIMPLE',
                          simpleTurnThreshold: Number.isFinite(
                            Number(savedFormData.simpleTurnThreshold)
                          )
                            ? Number(savedFormData.simpleTurnThreshold)
                            : 35,
                          simpleAppointmentRule: savedFormData.simpleAppointmentRule,
                        }}
                        orgId={orgId}
                      />
                    </div>
                  )}
                  <p className="text-[16px] text-clovie-muted">
                    Turn Turns on or off in{' '}
                    <Link
                      to="/$orgId/settings/business-setup/how-you-use-hayven"
                      params={{ orgId }}
                      className="font-medium text-clovie-brand underline-offset-2 hover:underline"
                    >
                      How you use Hayven
                    </Link>
                    .
                  </p>
                </div>
              </>
            )}
          </InfoCard>
        )}
      </BusinessSetupPageShell>
    </>
  );
}
